import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios, { AxiosResponse } from "axios";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Row,
  Table,
} from "reactstrap";
import DotsSpinner from "../Loader/DotsSpinner/DotsSpinner";

import { API } from "../../constants";

interface Props<TypeTable> {
  url: string;
  // tableHeaders: Array<string>;
  tableTitle: string;
  searchBox: boolean;
  itemsLimit: number;
  // sortByQuery: string;s
  customFunction: any;
  column: {
    key: string;
    label: string;
  }[];
  customColumns?: {
    heading: string;
    renderer: (item: TypeTable) => JSX.Element;
  }[];
  emptyLabel?: string;
}

export default function CustomTable<TypeTable>({
  url,
  tableTitle,
  searchBox,
  itemsLimit,
  customColumns,
  column,
  emptyLabel,
}: Props<TypeTable>): ReactElement {
  const [tableData, setTableData] = useState<Array<object>>([]); // stores data that is to be rendered
  const [filteredData, setFilteredData] = useState<Array<object>>([]); // stores only the defines data based on 'column' property
  const [totalItems, setTotalItems] = useState<number>(0); // stores total number of available items in DB
  const [currentPage, setCurrentPage] = useState<number>(1); // state for current page, defaults set to 1
  const [goToPage, setGoToPage] = useState<number>(1); // jump to a particular page
  const [isNextPage, setIsNextPage] = useState<boolean>(true); // if next page has content
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const columns = useMemo(
    () => (tableData?.length > 0 ? Object.keys(tableData[0]) : []),
    [tableData]
  );
  // --- FETCH DATA ---
  useEffect(() => {
    const source = axios.CancelToken.source();

    setIsLoading(true);
    axios
      .get(API + url + `&offset=${(currentPage - 1) * itemsLimit}`, {
        cancelToken: source.token,
      })
      .then(
        //  + itemsLimitQuery + `&offset=${currentPage * itemsLimit}` + sortByQuery
        (response: AxiosResponse) => {
          if (response.data?.data?.length !== 0) {
            setTableData(response?.data?.data);
            setTotalItems(
              response?.data?.meta?.filter_count || response?.data?.count
            );
          } else {
            setTableData([]);
            setTotalItems(0);
            setIsNextPage(false);
            // toast.warning("No Data Available!");
          }
          if (response.data?.data?.length === 0) {
            toast.warning("No Data Available!");
          }
          setGoToPage(1);
        },
        (error) => {
          if (axios.isCancel(error)) {
            console.log("Request canceled", error?.message);
          } else {
            toast.error("Can Not Fetch Data!");
            console.log("Error Caught: ", error?.response);
          }
        }
      )
      .finally(() => setIsLoading(false));

    return () => {
      source.cancel("Operation canceled.");
    };
  }, [url, currentPage, itemsLimit]);

  useEffect(() => {
    setCurrentPage(1);
  }, [url]);

  // --- FORMAT DATA TO CREATE THE TABLE ---
  useEffect(() => {
    if (column.length !== 0) {
      let tempData: any[] = [];
      tableData?.forEach((e, i) => {
        let dmg = {};
        column.forEach((v) => {
          if (columns?.includes(v.key)) {
            dmg[v.key] = e[v.key];
          }
        });
        // filteredData.push(dmg);
        tempData.push(dmg);
      });
      setFilteredData(tempData);
    } else {
      setFilteredData(tableData);
    }
  }, [tableData, column, columns]);

  // --- Increment decrement page starts ---
  const decrementCurrentPage = () => {
    setCurrentPage(Number(currentPage) - 1);
  };
  const incrementCurrentPage = () => {
    setCurrentPage(Number(currentPage) + 1);
  };
  // --- Increment decrement page ends ---

  // --- PAGINATES TO FIRST OR LAST PAGE ---
  const goToBoundaryPage = (page: number) => {
    // go to first/last page
    setIsLoading(true);
    axios
      .get(
        API +
          url +
          `&offset=${
            page === 1
              ? 0
              : (Math.ceil(totalItems / itemsLimit) - 1) * itemsLimit
          }`
      )
      .then((response) => {
        setTableData(response?.data?.data);
        setIsLoading(false);
        page === 1
          ? setCurrentPage(1)
          : setCurrentPage(Math.ceil(totalItems / itemsLimit));
      })
      .catch((error) => {
        toast.error("Can Not Fetch Activity!");
        console.log("Error Caught: ", error);
        setIsLoading(false);
      });
  };

  const onKeyDownHandler = useCallback(
    (e) => {
      // search on enter press
      if (e.keyCode === 13) {
        setCurrentPage(goToPage);
      }
    },
    [goToPage]
  );

  return (
    <React.Fragment>
      {tableData?.length > 0 ? (
        <Card className="shadow">
          {tableTitle && (
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <div className="col">
                  <h3 className="mb-0 text-muted">{tableTitle}</h3>
                </div>
              </Row>
            </CardHeader>
          )}

          {!isLoading ? (
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  {column?.map((eachCol, index) => (
                    <th key={index} scope="col">
                      {eachCol.label}
                    </th>
                  ))}

                  {customColumns?.map((column, index) => {
                    return (
                      <th key={"c_" + index} scope="col">
                        {column.heading}
                      </th>
                    );
                  })}

                  {/* 
                show all columns if column[] or customColumn[] is not provided
                */}
                  {column?.length === 0 &&
                    customColumns?.length === 0 &&
                    columns?.map((header, index) => (
                      <th key={index} scope="col">
                        {header.replace("_", " ") || header.replace(".", " ")}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {filteredData?.map((row, index) => (
                  <tr key={index}>
                    {column?.map((col, idx) => (
                      <td key={idx}>
                        {row[col.key] ? (
                          row[col.key]
                        ) : (
                          <span className="text-muted">Not Available</span>
                        )}
                      </td>
                    ))}

                    {customColumns?.map((column: any, ind) => (
                      <td key={ind}>{column.renderer(tableData[index])}</td>
                    ))}

                    {/*how all columns if column[] or customColumn[] is not provided*/}
                    {column?.length === 0 &&
                      customColumns?.length === 0 &&
                      columns?.map((col, idx) => <td key={idx}>{row[col]}</td>)}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <DotsSpinner />
          )}

          <CardFooter className="border-0 d-flex justify-content-between align-items-center">
            {totalItems !== 0 && (
              <p className="text-muted small">
                Total {totalItems} items are available
              </p>
            )}
            <Row className="align-items-center">
              <React.Fragment>
                <div className="d-flex justify-content-end align-items-center">
                  {currentPage > 1 && (
                    <>
                      <button
                        className="btn mr-1 text-primary small px-1"
                        onClick={() => goToBoundaryPage(1)}
                      >
                        Go To First
                      </button>
                      <i
                        className="fas fa-caret-left fa-2x mt-1 px-2 pointer"
                        onClick={decrementCurrentPage}
                        style={{ fontSize: 28 }}
                      ></i>
                    </>
                  )}

                  {currentPage !== Math.ceil(totalItems / itemsLimit) && (
                    <>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 60, minWidth: 50 }}
                        value={goToPage === 1 ? currentPage : goToPage}
                        onChange={(e) => setGoToPage(Number(e.target.value))}
                        onKeyDown={onKeyDownHandler}
                      />
                      {isNextPage && (
                        <i
                          className="fas fa-caret-right mt-1 px-2 pointer"
                          onClick={incrementCurrentPage}
                          style={{ fontSize: 28 }}
                        ></i>
                      )}
                      <button
                        className="btn ml-1 text-primary small px-1"
                        onClick={() => goToBoundaryPage(2)}
                      >
                        Go To Last
                      </button>
                    </>
                  )}
                </div>
              </React.Fragment>
            </Row>
          </CardFooter>
        </Card>
      ) : (
        !isLoading && (
          <div>
            <p className="alert-heading d-flex justify-content-center align-items-center">
              {emptyLabel ? emptyLabel : "Oops, there is no data available!"}
            </p>
            {currentPage > 1 && (
              <Button onClick={() => setCurrentPage(1)}>
                Return To First Page
              </Button>
            )}
            <hr />
            {/* <p className="mb-0">There might be some problem. Please try again.</p> */}
          </div>
        )
      )}
    </React.Fragment>
  );
}
